import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import { PermissionEnum, SecurityUsageFilterInDTO, SecurityUsageOutDTO } from '@api/ringil-api.v2';
import { InputText } from 'primereact/inputtext';
import RingilButton from '@components/ringil3/Buttons/RingilButton';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import SecurityUsersDialog from '@components/admin/security/SecurityUsersDialog';

interface ComponentProps {}

const SecurityUsage: React.FC<ComponentProps> = () => {
  const { getSecurityUsage } = useContext(ApiContext);
  const { tr } = useTranslationLgs();
  const [userIdInput, setUserIdInput] = useState<string>('');
  const [emailInput, setEmailInput] = useState<string>('');
  const [permissionFilter, setPermissionFilter] = useState<string>('');
  const [filter, setFilter] = useState<SecurityUsageFilterInDTO>({});
  const [usersDialogPermission, setUsersDialogPermission] = useState<PermissionEnum | null>(null);
  const [data, setData] = useState<SecurityUsageOutDTO | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const acRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const ac = new AbortController();
    acRef.current?.abort();
    acRef.current = ac;
    setLoading(true);
    getSecurityUsage(
      filter,
      d => {
        if (ac.signal.aborted) return;
        setData(d);
        setLoading(false);
      },
      null,
      {
        onError: (_error, showStandardError) => {
          if (!ac.signal.aborted) setLoading(false);
          showStandardError();
        },
      },
    );
    return () => ac.abort();
  }, [filter]);

  const rolesById = useMemo(() => {
    const m = new Map<string, { id: string; name: string; groups: { id: string; name: string }[] }>();
    data?.roles?.forEach(r => m.set(r.id, r));
    return m;
  }, [data]);

  const sortedPermissions = useMemo(() => {
    const q = permissionFilter.trim().toLowerCase();
    const all = (data?.permissions ?? []).slice().sort((a, b) => a.permission.localeCompare(b.permission));
    if (!q) return all;
    return all.filter(p => p.permission.toLowerCase().includes(q) || (p.translateKeyName ?? '').toLowerCase().includes(q));
  }, [data, permissionFilter]);

  const applyFilter = () => {
    setFilter({
      userId: userIdInput.trim() || undefined,
      email: emailInput.trim() || undefined,
    });
  };

  const clearFilter = () => {
    setUserIdInput('');
    setEmailInput('');
    setFilter({});
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') applyFilter();
  };

  const hasActiveFilter = !!(filter.userId || filter.email);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row gap-3 items-end flex-wrap">
        <div className="flex flex-col">
          <label className="text-xs text-content-secondary mb-1">{tr('SecurityUsage.userId', 'User ID')}</label>
          <InputText value={userIdInput} onChange={e => setUserIdInput(e.target.value)} onKeyDown={onKeyDown} placeholder="UUID" />
        </div>
        <div className="flex flex-col">
          <label className="text-xs text-content-secondary mb-1">{tr('SecurityUsage.email', 'Email')}</label>
          <InputText value={emailInput} onChange={e => setEmailInput(e.target.value)} onKeyDown={onKeyDown} placeholder="user@example.com" />
        </div>
        <RingilButton mode={'filled-rectangle'} label={tr('SecurityUsage.apply', 'Apply filter')} onClick={applyFilter} />
        {hasActiveFilter && <RingilButton mode={'text-only'} label={tr('SecurityUsage.clear', 'Clear')} onClick={clearFilter} />}
      </div>

      {hasActiveFilter && (
        <div className="text-xs text-content-secondary">
          {tr('SecurityUsage.activeFilter', 'Filter active:')}{' '}
          {filter.userId && <span className="font-mono">userId={filter.userId}</span>}
          {filter.userId && filter.email && ' • '}
          {filter.email && <span className="font-mono">email={filter.email}</span>}
        </div>
      )}

      {loading && <div className="italic text-content-secondary">{tr('SecurityUsage.loading', 'Loading…')}</div>}

      {!loading && data && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-row items-end gap-3 flex-wrap">
            <div className="flex flex-col">
              <label className="text-xs text-content-secondary mb-1">{tr('SecurityUsage.permissionFilter', 'Filter permissions (UI)')}</label>
              <InputText
                value={permissionFilter}
                onChange={e => setPermissionFilter(e.target.value)}
                placeholder={tr('SecurityUsage.permissionFilterPlaceholder', 'permission key or translation…')}
              />
            </div>
            {permissionFilter && (
              <RingilButton mode={'text-only'} label={tr('SecurityUsage.clearPermissionFilter', 'Clear')} onClick={() => setPermissionFilter('')} />
            )}
          </div>
          <h2 className="text-lg font-bold">
            {tr('SecurityUsage.permissionsTitle', 'Permissions')} ({sortedPermissions.length}
            {permissionFilter && data.permissions.length !== sortedPermissions.length ? ` / ${data.permissions.length}` : ''})
          </h2>
          {sortedPermissions.length === 0 && (
            <div className="italic text-content-secondary">{tr('SecurityUsage.noResults', 'No permissions match the filter.')}</div>
          )}
          {sortedPermissions.map(p => (
            <div key={p.permission} className="border rounded p-3 bg-white">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono font-bold">{p.permission}</span>
                {p.translateKeyName && (
                  <span className="text-content-secondary text-sm">{tr(p.translateKeyName, p.translateKeyName)}</span>
                )}
                {p.deprecated && (
                  <span className="px-1.5 py-0.5 text-[10px] uppercase font-bold rounded bg-red-100 text-red-700">deprecated</span>
                )}
                {typeof p.userCount === 'number' && (
                  <button
                    type="button"
                    className="ml-auto px-2 py-0.5 text-xs rounded border border-content-brandMedium text-content-brand hover:bg-content-brandLight cursor-pointer"
                    onClick={() => setUsersDialogPermission(p.permission)}
                    title={tr('SecurityUsage.showUsers', 'Show users')}
                  >
                    {tr('SecurityUsage.users', 'Users')}: {p.userCount}
                  </button>
                )}
              </div>

              <div className="mt-2 pl-2">
                <div className="text-sm font-bold text-content-secondary">
                  {tr('SecurityUsage.roles', 'Roles')} ({p.roles.length})
                </div>
                {p.roles.length === 0 ? (
                  <div className="text-xs italic text-content-tertiary pl-3">{tr('SecurityUsage.noRoles', 'no roles')}</div>
                ) : (
                  <ul className="list-disc pl-6 mt-1">
                    {p.roles.map(r => {
                      const fullRole = rolesById.get(r.id);
                      return (
                        <li key={r.id} className="text-sm">
                          <span className="font-medium">{r.name}</span>
                          {fullRole && fullRole.groups.length > 0 ? (
                            <span className="text-content-secondary text-xs">
                              {' '}
                              → {tr('SecurityUsage.groups', 'groups')}:{' '}
                              {fullRole.groups.map(g => g.name).join(', ')}
                            </span>
                          ) : (
                            <span className="text-content-tertiary text-xs"> → {tr('SecurityUsage.noGroups', 'no groups')}</span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {p.groups && p.groups.length > 0 && (
                <div className="mt-2 pl-2">
                  <div className="text-sm font-bold text-content-secondary">
                    {tr('SecurityUsage.directGroups', 'Direct groups')} ({p.groups.length})
                  </div>
                  <ul className="list-disc pl-6 mt-1">
                    {p.groups.map(g => (
                      <li key={g.id} className="text-sm">{g.name}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <SecurityUsersDialog
        visible={usersDialogPermission !== null}
        onHide={() => setUsersDialogPermission(null)}
        filter={usersDialogPermission !== null ? { permission: usersDialogPermission } : null}
        title={usersDialogPermission ? `${tr('SecurityUsage.usersFor', 'Users for')} ${usersDialogPermission}` : undefined}
      />
    </div>
  );
};

export default SecurityUsage;